import { api } from "./client";
import type {
  CursorPage,
  DomainDetail,
  DomainSummary,
} from "./types";

export interface DomainFilters {
  status?: string;
  tag?: string[];
  run_id?: string;
  q?: string;
  // Structured failure-reason codes — `UnreachableReason` values, optionally
  // `_heavy`-suffixed. Only meaningful when status === "unreachable".
  unreachable_reason?: string[];
  limit?: number;
}

const appendMulti = (
  qs: URLSearchParams,
  key: string,
  values: string[] | undefined,
): void => {
  if (!values) return;
  for (const v of values) qs.append(key, v);
};

const writeCommonFilters = (qs: URLSearchParams, filters: DomainFilters): void => {
  if (filters.status) qs.set("status", filters.status);
  appendMulti(qs, "tag", filters.tag);
  if (filters.run_id) qs.set("run_id", filters.run_id);
  if (filters.q) qs.set("q", filters.q);
  appendMulti(qs, "unreachable_reason", filters.unreachable_reason);
};

export const domainsPage = (
  cursor: string | null,
  filters: DomainFilters = {},
): Promise<CursorPage<DomainSummary>> => {
  const qs = new URLSearchParams();
  if (cursor) qs.set("cursor", cursor);
  writeCommonFilters(qs, filters);
  qs.set("limit", String(filters.limit ?? 100));
  return api(`/api/domains?${qs}`);
};

export const countDomains = (filters: DomainFilters = {}) => {
  const qs = new URLSearchParams();
  writeCommonFilters(qs, filters);
  return api<{ count: number; capped: boolean }>(`/api/domains/count?${qs}`);
};

export const getDomain = (id: string) =>
  api<DomainDetail>(`/api/domains/${id}`);

export const retryDomain = (id: string) =>
  api<{ enqueued: boolean }>(`/api/domains/${id}/retry`, { method: "POST" });

export interface BulkBody {
  ids?: string[];
  filter?: DomainFilters;
  reason?: string;
}

export interface BulkResult {
  affected: number;
  async: boolean;
  capped: boolean;
}

export const bulkRetry = (body: BulkBody) =>
  api<BulkResult>("/api/domains/bulk/retry", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const bulkIgnore = (body: BulkBody) =>
  api<BulkResult>("/api/domains/bulk/ignore", {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * Build the URL for `GET /api/domains/export`. Browser-triggered
 * download (via `window.location.assign` or a hidden anchor) — no
 * fetch wrapper, the server's `Content-Disposition` header drives the
 * "Save as" flow. Same filter shape as the list endpoint.
 */
export const exportDomainsUrl = (
  filters: DomainFilters,
  format: "csv" | "jsonl",
): string => {
  const qs = new URLSearchParams();
  writeCommonFilters(qs, filters);
  qs.set("format", format);
  return `/api/domains/export?${qs}`;
};
